import { Component, OnInit, AfterViewInit, Inject, Optional, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';

import { PropertyService } from '../../services/property.service';
import { LesseeService } from '../../services/lessee.service';
import { NavigationContextService } from '../../services/navigation-context.service';
import { InvoicesComponent } from '../../common/invoices/invoices.component';

import { suggestLateFee, INVOICE_MONTHS_BACK } from './invoice-helpers';

@Component({
  selector: 'app-admin-invoices',
  templateUrl: './admin-invoices.component.html',
  styleUrls: ['./admin-invoices.component.scss']
})
export class AdminInvoicesComponent implements OnInit, AfterViewInit {
  invoice = { items: [] } as any;
  selectedTabIndex = 0;
  lease: any;
  months: Array<{label: string, month: number, year: number}> = [];
  suggestedLateFee: { quantity: number, rate: number, amount: number } | null = null;
  selectedMonth: {label: string, month: number, year: number};
  property: any;
  allInvoices: any[] = [];
  filteredInvoices: any[] = [];
  loading = false;
  displayedColumns: string[] = ['property', 'dueDate', 'amount', 'status', 'remainingBalance', 'actions'];
  dataSource = new MatTableDataSource<any>();
  @ViewChild(InvoicesComponent) invoicesList: InvoicesComponent;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  // Pagination properties
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Filter properties
  allProperties: any[] = [];
  selectedPropertyId: string | null = null;
  searchText: string = '';
  selectedDateFilter: string = 'all'; // 'all', 'thisMonth', 'lastMonth', 'custom'
  customStartDate: string = '';
  customEndDate: string = '';
  
  constructor(private propertyService: PropertyService,
              private lesseeService: LesseeService,
              @Optional() public dialogRef: MatDialogRef<AdminInvoicesComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private route: ActivatedRoute,
              private router: Router,
              private navContext: NavigationContextService) { }

  ngOnInit() {
    const propertyId = this.route.snapshot.queryParamMap.get('propertyId');
    if (propertyId) {
      // If propertyId is provided, show property-specific view
      this.propertyService.getPublicProperty(propertyId).then(p => {
        this.property = p;
        this.propertyService.getActiveLease(this.property).then((lease) => {
          this.lease = lease;
          this.buildMonths();
        }).catch(() => {
          this.lease = null;
        });
      });
    } else {
      // Show all organization invoices
      this.loadAllInvoices();
    }
  }

  loadAllInvoices() {
    this.loading = true;
    const dateRange = this.getDateRange();
    Promise.all([
      this.propertyService.getAllInvoices(
        this.currentPage,
        this.pageSize,
        this.selectedPropertyId || undefined,
        undefined,
        dateRange.startDate,
        dateRange.endDate
      ),
      this.propertyService.getProperties()
    ]).then(([response, properties]: [any, any[]]) => {
      this.loading = false;
      
      // Handle paginated response
      if (response && response.data && Array.isArray(response.data)) {
        const expectedPageSize = response.pageSize || this.pageSize || 10;
        
        // CRITICAL: Ensure we never display more items than the pageSize
        // This is a safety measure in case the backend returns too many items
        const limitedData = response.data.slice(0, expectedPageSize);
        
        if (response.data.length > expectedPageSize) {
          console.error(`[AdminInvoices] ERROR: Backend returned ${response.data.length} items but pageSize is ${expectedPageSize}!`);
          console.error(`[AdminInvoices] Applying frontend limit: ${limitedData.length} items`);
        }
        
        // IMPORTANT: Replace allInvoices with ONLY the current page's data (limited)
        // Preserve statementId and ensure payments are properly mapped
        this.allInvoices = limitedData.map(inv => {
          // Preserve statementId (handle both camelCase and snake_case)
          if (inv.statement_id && !inv.statementId) {
            inv.statementId = inv.statement_id;
          }
          // Ensure payments have statementId mapped correctly
          if (inv.payments && Array.isArray(inv.payments)) {
            inv.payments = inv.payments.map((p: any) => {
              if (p.statement_id && !p.statementId) {
                p.statementId = p.statement_id;
              }
              return p;
            });
          }
          return inv;
        });
        this.totalItems = response.total || 0;
        this.currentPage = response.page || 1;
        this.pageSize = expectedPageSize;
        console.log(`[AdminInvoices] Loaded page ${this.currentPage}, showing ${this.allInvoices.length} of ${this.totalItems} total invoices (pageSize: ${this.pageSize})`);
        console.log(`[AdminInvoices] Response structure:`, { 
          hasData: !!response.data, 
          dataLength: response.data.length, 
          limitedLength: limitedData.length,
          total: response.total, 
          page: response.page, 
          pageSize: response.pageSize 
        });
      } else {
        // Fallback for non-paginated response
        this.allInvoices = Array.isArray(response) ? response : [];
        this.totalItems = this.allInvoices.length;
        console.warn('[AdminInvoices] Received non-paginated response, showing all', this.allInvoices.length, 'invoices');
        console.warn('[AdminInvoices] Response was:', response);
      }
      
      this.allProperties = Array.isArray(properties) ? properties : [];
      // Apply client-side search filter on current page data
      this.applyAllFilters();
    }).catch((error) => {
      console.error('Error loading invoices:', error);
      this.loading = false;
      this.allInvoices = [];
      this.allProperties = [];
      this.totalItems = 0;
    });
  }

  get canCreateInvoice(): boolean {
    return !!(this.property && this.property.id && this.lease && this.lease.id);
  }

  getDateRange(): { startDate?: string, endDate?: string } {
    if (this.selectedDateFilter === 'all') {
      return {};
    }
    return {
      startDate: this.customStartDate || undefined,
      endDate: this.customEndDate || undefined
    };
  }
  
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAllInvoices();
  }

  onRowClick(event: MouseEvent, invoice: any) {
    // Don't navigate if clicking on a button or inside a button
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON' || target.closest('mat-icon')) {
      return;
    }
    this.viewInvoice(invoice);
  }

  viewInvoice(invoice: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (invoice.property && invoice.property.id) {
      // Navigate to invoice PDF view with property context
      this.router.navigate(['/invoice', invoice.id, 'pdf'], { queryParams: { propertyId: invoice.property.id } });
    } else if (invoice.propertyId) {
      // Fallback if property info is missing but propertyId exists
      this.router.navigate(['/invoice', invoice.id, 'pdf'], { queryParams: { propertyId: invoice.propertyId } });
    } else {
      // Fallback to PDF view without property context
      this.router.navigate(['/invoice', invoice.id, 'pdf']);
    }
  }

  downloadInvoice(invoice: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    const prop = invoice.property || (invoice.propertyId ? { id: invoice.propertyId } : null);
    let promise: Promise<Blob>;
    if (prop && prop.id) {
      promise = this.propertyService.getInvoicePdf(prop, invoice.id);
    } else {
      promise = this.lesseeService.getInvoicePdf(invoice.id);
    }
    promise.then((blob: Blob) => {
      const safe = (invoice.dueDate || invoice.id || 'invoice').toString().replace(/[^A-Za-z0-9_.-]/g, '');
      const fileName = `invoice-${safe}.pdf`;
      this.saveBlobReliably(blob, fileName);
    }).catch((err) => {
      console.error('Failed to download invoice PDF (admin):', err);
      alert('Unable to download the invoice PDF. Please try again.');
    });
  }

  /** Web Share File (iOS-friendly Save) with blob fallback. */
  private saveBlobReliably(blob: Blob, fileName: string) {
    const nav: any = (typeof navigator !== 'undefined') ? navigator : null;
    const tryWebShareFile = async (): Promise<boolean> => {
      if (!nav || typeof nav.share !== 'function') { return false; }
      try {
        const file = new File([blob], fileName, { type: 'application/pdf', lastModified: Date.now() });
        if (!nav.canShare || nav.canShare({ files: [file] })) {
          await nav.share({ files: [file] });
          return true;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') { return true; }
      }
      return false;
    };
    tryWebShareFile().then((did) => {
      if (did) { return; }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    });
  }

  generatePdf(invoice: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    // Get property for the invoice
    const invoiceProperty = invoice.property || (invoice.propertyId ? { id: invoice.propertyId } : null);
    
    if (!invoiceProperty) {
      alert('Cannot generate PDF: Property information is missing');
      return;
    }

    this.propertyService.generateInvoicePdf(invoiceProperty, invoice.id).then((res: any) => {
      if (res) {
          // Update the invoice in the list
          const index = this.allInvoices.findIndex(inv => inv.id === invoice.id);
          if (index !== -1) {
            this.allInvoices[index] = res;
            this.applyAllFilters();
          }
          // Also refresh the invoices list component if it exists
          if (this.invoicesList) {
            this.invoicesList.reload();
          }
      }
    }).catch((error) => {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    });
  }

  deletePdf(invoice: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!confirm('Are you sure you want to delete this PDF? You can regenerate it later.')) {
      return;
    }
    if (invoice.property && invoice.property.id) {
      this.propertyService.deleteInvoicePdf(invoice.property, invoice.id).then((res: any) => {
        if (res) {
          // Update the invoice in the list
          const index = this.allInvoices.findIndex(inv => inv.id === invoice.id);
          if (index !== -1) {
            this.allInvoices[index] = res;
            this.applyAllFilters();
          }
          // Also refresh the invoices list component if it exists
          if (this.invoicesList) {
            this.invoicesList.reload();
          }
        }
      }).catch((error) => {
        console.error('Error deleting PDF:', error);
        alert('Failed to delete PDF. Please try again.');
      });
    }
  }

  hasStatementId(item: any): boolean {
    if (!item) return false;
    // Check both camelCase and snake_case, and also check for truthy values (not null, undefined, or empty string)
    const statementId = item.statementId || item.statement_id;
    const hasId = !!(statementId && statementId !== null && statementId !== undefined && statementId !== '');
    
    // Debug: log only if we find a statementId (to reduce spam)
    if (hasId) {
      console.log(`[AdminInvoices] Found invoice with statementId: ${item.id}, statementId: ${statementId}`);
    }
    
    // Also check if invoice has payments with statement_id (invoices created from statements often have statement-linked payments)
    if (!hasId && item.payments && Array.isArray(item.payments)) {
      const hasStatementPayment = item.payments.some((p: any) => {
        const paymentStatementId = p.statementId || p.statement_id;
        return !!(paymentStatementId && paymentStatementId !== null && paymentStatementId !== undefined && paymentStatementId !== '');
      });
      if (hasStatementPayment) {
        console.log(`[AdminInvoices] Found statement payment for invoice ${item.id}`);
        return true;
      }
    }
    
    return hasId;
  }

  deleteInvoice(invoice: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (this.hasStatementId(invoice)) {
      alert('Cannot delete invoice created from a statement. Remove the statement instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this invoice?')) {
      return;
    }
    if (invoice.property && invoice.property.id) {
      this.propertyService.deleteInvoice(invoice.property, invoice).then(() => {
        this.loadAllInvoices();
      }).catch((error) => {
        console.error('Error deleting invoice:', error);
        const errorMessage = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
        if (errorMessage.includes('statement')) {
          alert('Cannot delete invoice created from a statement. Remove the statement instead.');
        } else {
          alert('Failed to delete invoice. Please try again.');
        }
      });
    }
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
    if (this.paginator) {
      // For server-side pagination, we handle pagination events manually
      this.paginator.page.subscribe(event => {
        this.onPageChange(event);
      });
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue.trim().toLowerCase();
    // For server-side, we'll filter client-side on the current page
    this.applyAllFilters();
  }

  onPropertyFilterChange(propertyId: string | null) {
    this.selectedPropertyId = propertyId;
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllInvoices();
  }

  onDateFilterChange(filterType: string) {
    this.selectedDateFilter = filterType;
    if (filterType === 'thisMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (filterType === 'lastMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    } else if (filterType === 'all') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllInvoices();
  }

  onCustomDateChange() {
    if (this.customStartDate && this.customEndDate) {
      this.selectedDateFilter = 'custom';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllInvoices();
  }

  applyAllFilters() {
    // With server-side pagination, property and date filters are handled server-side.
    // Only apply search text filter client-side on the current page
    let filtered = [...this.allInvoices];

    // Filter by search text (client-side on current page only)
    if (this.searchText) {
      filtered = filtered.filter(inv => {
        const searchLower = this.searchText.toLowerCase();
        const propertyName = inv.property ? 
          `${inv.property.addressLine1 || ''} ${inv.property.city || ''} ${inv.property.state || ''}`.toLowerCase() : '';
        const amount = inv.amount ? String(inv.amount).toLowerCase() : '';
        const status = inv.status ? inv.status.toLowerCase() : '';
        const balance = inv.remainingBalance ? String(inv.remainingBalance).toLowerCase() : '';
        
        return propertyName.includes(searchLower) ||
               amount.includes(searchLower) ||
               status.includes(searchLower) ||
               balance.includes(searchLower);
      });
    }

    this.filteredInvoices = filtered;
    // CRITICAL: Only set the filtered data (which should only be current page)
    // This ensures the table only shows the current page's items
    this.dataSource.data = this.filteredInvoices;
    console.log(`[AdminInvoices] applyAllFilters: Setting dataSource.data to ${this.filteredInvoices.length} items (should match page size ${this.pageSize})`);
    
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource for server-side pagination
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
  }

  clearFilters() {
    this.selectedPropertyId = null;
    this.searchText = '';
    this.selectedDateFilter = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    this.currentPage = 1; // Reset to first page when clearing filters
    this.loadAllInvoices();
  }

  buildMonths() {
    if(!this.lease) {
      return;
    }

    const months = [];

    // Month-to-month (or missing term dates) uses a rolling window so future
    // invoice months like the current month remain available.
    const isMonthToMonth = this.lease.type === 'M2M' || (!this.lease.startDate && !this.lease.endDate);

    if(isMonthToMonth) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - INVOICE_MONTHS_BACK, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 12, 1);
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      while(current <= end) {
        months.push({
          label: current.toLocaleString('default', { month: 'long', year: 'numeric' }),
          month: current.getMonth(),
          year: current.getFullYear()
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
    } else {
      const start = new Date(this.lease.startDate);
      const end = new Date(this.lease.endDate);
      if(!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        let current = new Date(start.getFullYear(), start.getMonth(), 1);
        while(current <= end) {
          months.push({
            label: current.toLocaleString('default', { month: 'long', year: 'numeric' }),
            month: current.getMonth(),
            year: current.getFullYear()
          });
          current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
        }
      }
    }

    this.months = months;
  }

  onMonthChange() {
    if(!this.selectedMonth || !this.lease) {
      return;
    }

    const dueDate = new Date(this.selectedMonth.year, this.selectedMonth.month, this.lease.invoiceDayOfMonth);
    const lateDate = new Date(this.selectedMonth.year, this.selectedMonth.month, this.lease.invoiceDueDayOfMonth);

    const formatDate = (d: Date) => {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const items: any[] = [{ description: 'Rent', quantity: 1, rate: this.lease.monthlyCost, amount: this.lease.monthlyCost }];

    // Never auto-add a late fee (backdated invoices would get e.g. 191 days x $10).
    // Offer a capped one instead; the admin adds it explicitly.
    this.suggestedLateFee = suggestLateFee(lateDate, new Date(), this.lease.lateFeePercent);

    this.invoice = {
      items: items,
      amount: 0,
      date: formatDate(new Date()),
      dueDate: formatDate(dueDate),
      lateDate: formatDate(lateDate),
      lateFee: 0,
      remainingBalance: 0,
      status: ''
    } as any;
    this.updateStatus();

    this.updateTotals();
  }

  addSuggestedLateFee() {
    if (!this.suggestedLateFee || !this.invoice) {
      return;
    }
    this.invoice.items.push({ description: 'Late Fee', ...this.suggestedLateFee });
    this.suggestedLateFee = null;
    this.updateTotals();
  }

  addItem() {
    if(!this.invoice.items) {
      this.invoice.items = [];
    }
    this.invoice.items.push({ description: '', quantity: 1, rate: 0, amount: 0 });
  }

  removeItem(index: number) {
    this.invoice.items.splice(index, 1);
    this.updateTotals();
  }

  updateItemAmount(item) {
    item.amount = (item.quantity || 0) * (item.rate || 0);
    this.updateTotals();
  }

  updateTotals() {
    if(!this.invoice.items || this.invoice.items.length === 0) {
      this.invoice.amount = 0;
    } else {
      this.invoice.amount = this.invoice.items.reduce((t, i) => t + (i.amount || 0), 0);
    }
    this.invoice.remainingBalance = this.invoice.amount;
  }

  updateStatus() {
    if(!this.invoice || !this.invoice.dueDate) {
      this.invoice.status = 'NEW';
      return;
    }
    const due = new Date(this.invoice.dueDate);
    const now = new Date();
    this.invoice.status = (due < new Date(now.getFullYear(), now.getMonth(), now.getDate())) ? 'DUE' : 'NEW';
  }

  createInvoice(){
    if (!this.canCreateInvoice) {
      alert('Select a property with an active lease before creating an invoice.');
      return;
    }
    this.updateStatus();
    const target = this.property;
    this.propertyService.createInvoice(this.invoice, target).then(() => {
      this.selectedTabIndex = 0;
      this.invoice = { items: [] };
      if(this.invoicesList){
        this.invoicesList.reload();
      }
      // Refresh all invoices list if showing organization-wide view
      if (!this.property) {
        this.loadAllInvoices();
      }
    }).catch((error) => {
      console.error('Error creating invoice:', error);
      const errorMessage = (error && error.error && error.error.error)
        ? error.error.error
        : (error.message || 'Failed to create invoice. Please try again.');
      alert(errorMessage);
    });
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
    } else {
      this.navContext.goToDashboard();
    }
  }

}
